import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Grid, Card, Typography, CircularProgress, Chip, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PendingIcon from "@mui/icons-material/Pending";
import EventIcon from "@mui/icons-material/Event";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../utils/api";

const StatCard = ({ icon, label, value, color = "#2f63ad", bg = "rgba(47,99,173,0.08)" }) => (
  <Card sx={{ borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5, height:"100%" }}>
    <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
      <Box sx={{ width:44, height:44, borderRadius:"12px", bgcolor:bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        {React.cloneElement(icon, { sx:{ color, fontSize:22 } })}
      </Box>
      <Box>
        <Typography variant="h4" sx={{ fontWeight:800, color:"#223142", lineHeight:1 }}>{value ?? "N/A"}</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{label}</Typography>
      </Box>
    </Box>
  </Card>
);

const GateDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/gate-dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtTime = (d) => d ? new Date(d).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }) : "N/A";
  const fmtDT   = (d) => d ? new Date(d).toLocaleString() : "N/A";

  const hourly = Array.from({ length:24 }, (_, h) => {
    const found = data?.hourlyTraffic?.find(r => r.hour === h);
    return { hour: `${h}:00`, count: found?.count || 0 };
  });

  if (loading) return (
    <Box sx={{ display:"flex", justifyContent:"center", py:6 }}><CircularProgress /></Box>
  );

  const s = data?.stats || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:3 }}>
        <Typography variant="h5" sx={{ fontWeight:800, color:"#223142" }}>Gate Dashboard</Typography>
        <Tooltip title="Refresh"><IconButton onClick={load} size="small"><RefreshIcon /></IconButton></Tooltip>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb:3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<DirectionsWalkIcon />} label="Currently Inside" value={s.insideNow} color="#2f63ad" bg="rgba(47,99,173,0.08)" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<LoginIcon />} label="Arrivals Today" value={s.arrivalsToday} color="#0f766e" bg="rgba(15,118,110,0.08)" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<LogoutIcon />} label="Departures Today" value={s.departuresToday} color="#b45309" bg="rgba(180,83,9,0.08)" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<EventIcon />} label="Expected Today" value={s.expectedToday} color="#7c3aed" bg="rgba(124,58,237,0.08)" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<PendingIcon />} label="Pending Approval" value={s.pendingApproval} color="#dc2626" bg="rgba(220,38,38,0.08)" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<CalendarViewWeekIcon />} label="This Week" value={s.thisWeekTotal} color="#0369a1" bg="rgba(3,105,161,0.08)" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Hourly Traffic Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Arrivals by Hour - Today</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={hourly} margin={{ top:0, right:0, left:-20, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="hour" tick={{ fontSize:10, fill:"#6b778c" }} interval={3} />
                <YAxis tick={{ fontSize:11, fill:"#6b778c" }} allowDecimals={false} />
                <RTooltip />
                <Bar dataKey="count" fill="#2f63ad" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Currently Inside */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5, height:"100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Currently Inside</Typography>
            {(data?.currentlyInside || []).length === 0 ? (
              <Typography variant="body2" sx={{ color:"#6b778c" }}>No visitors currently inside.</Typography>
            ) : (
              <Box sx={{ maxHeight:200, overflowY:"auto" }}>
                {data.currentlyInside.map((v) => (
                  <Box key={v.VisitID} sx={{ py:1, borderBottom:"1px solid rgba(0,0,0,0.05)" }}>
                    <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>
                      {v.FirstName} {v.LastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color:"#6b778c" }}>
                      In: {fmtTime(v.CheckInTime)} &nbsp;|&nbsp; {v.VehiclePlate || "No plate"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Recent Activity</Typography>
            <Paper elevation={0} sx={{ borderRadius:"10px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.08)" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor:"rgba(47,99,173,0.04)" }}>
                  <TableRow>
                    {["Visitor","Member ID","Arrival","Check-In","Check-Out","Status"].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, color:"#223142", fontSize:12 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recentActivity || []).map((v) => (
                    <TableRow key={v.VisitID} hover>
                      <TableCell sx={{ fontWeight:600, color:"#223142" }}>{v.FirstName} {v.LastName}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{v.ClubMemberID || "N/A"}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{fmtDT(v.ArrivalDate)}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{fmtTime(v.CheckInTime)}</TableCell>
                      <TableCell sx={{ color:"#6b778c", fontSize:12 }}>{fmtTime(v.CheckOutTime)}</TableCell>
                      <TableCell>
                        <Chip label={v.StatusName || "Unknown"} size="small"
                          sx={{ fontWeight:600, fontSize:11, borderRadius:"8px",
                            bgcolor: v.StatusName === "Checked-In" ? "rgba(15,118,110,0.12)" : "rgba(47,99,173,0.08)",
                            color:   v.StatusName === "Checked-In" ? "#0f766e" : "#2f63ad" }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GateDashboard;
